<template>
  <v-main>
    <v-container fluid>
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-title>
              <h3 class="font-weight-bold">Payment Receipts</h3>
              <v-btn
                class="mx-2 mb-2"
                fab
                x-small
                color="primary"
                to="/pay-credit-balance/create"
              >
                <v-icon dark> mdi-plus </v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col md="6">
                  <v-text-field
                    solo
                    rounded
                    v-model="filter.search_keyword"
                    label="Search by Receipt No, Transaction ID, UTR, Amount, or User Details..."
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    @click:clear="
                      (filter.search_keyword = null), (page = 1), getData()
                    "
                    @keydown.enter="(page = 1), getData()"
                    @input="onSearchInput"
                  ></v-text-field>
                </v-col>
                <v-col sm="3">
                  <v-autocomplete
                    ref="status"
                    solo
                    rounded
                    v-model="filter.status"
                    :items="statusOptions"
                    label="Status"
                    @input="(page = 1), getData()"
                    prepend-inner-icon="mdi-toggle-switch"
                    clearable
                  ></v-autocomplete>
                </v-col>
                <v-col sm="3">
                  <v-menu
                    v-model="date_menu"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        solo
                        rounded
                        v-model="filter.date"
                        label="Date"
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        clearable
                        @click:clear="
                          () => {
                            filter.date = null;
                            page = 1;
                            getData();
                          }
                        "
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="filter.date"
                      @input="(date_menu = false), (page = 1), getData()"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>
              <v-progress-linear
                v-if="isLoading"
                indeterminate
                color="primary"
              ></v-progress-linear>
              <v-simple-table v-else class="striped-table">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left sr_no">Sr No</th>
                      <th class="text-left" style="min-width: 120px; width: 120px;">Receipt No</th>
                      <th class="text-left" style="min-width: 180px; width: 180px;">User Name</th>
                      <th class="text-left">Phone Number</th>
                      <th class="text-left">Amount</th>
                      <th class="text-left">Mode of Payment</th>
                      <th class="text-left">Merchant Transaction ID</th>
                      <th class="text-left">Order ID</th>
                      <th class="text-left" style="min-width: 110px; width: 110px;">Date</th>
                    </tr>
                  </thead>
                  <tbody v-if="paymentReceipts.length != 0">
                    <tr
                      v-for="(receipt, i) in paymentReceipts"
                      :key="`receipt_${i}`"
                      :class="{
                        'table-row-even': i % 2 === 0,
                        'table-row-odd': i % 2 !== 0,
                        'table-row-last': paymentReceipts.length - 1 == i,
                      }"
                    >
                      <td style="min-width: 85px">
                        {{ (page - 1) * 10 + i + 1 }}
                      </td>
                      <td style="min-width: 120px; width: 120px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">{{ receipt.receipt_no || 'N/A' }}</td>
                      <td style="min-width: 180px; width: 180px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">{{ receipt.user ? receipt.user.first_name + ' ' + (receipt.user.last_name || '') : 'N/A' }}</td>
                      <td>{{ receipt.user ? receipt.user.phone || 'N/A' : 'N/A' }}</td>
                      <td>₹{{ receipt.amount_received || '0.00' }}</td>
                      <td>{{ getModeOfPaymentText(receipt.mode_of_payment) }}</td>
                      <td>{{ receipt.merchant_transaction_id || 'N/A' }}</td>
                      <td>{{ receipt.order_id || 'N/A' }}</td>
                      <td style="min-width: 110px; width: 110px; white-space: nowrap;">{{ formatDate(receipt.date_of_payment) }}</td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td colspan="9" class="text-center">No data found</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <v-pagination
                v-if="count > 10"
                v-model="page"
                :length="Math.ceil(count / 10)"
                @input="getData"
                total-visible="7"
                color="primary"
              ></v-pagination>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      isLoading: false,
      paymentReceipts: [],
      count: 0,
      page: 1,
      date_menu: false,
      searchTimeout: null,
      filter: {
        search_keyword: "",
        status: "",
        date: "",
      },
      statusOptions: [
        { text: "All", value: "" },
        { text: "Success", value: "PAYMENT_SUCCESS" },
        { text: "Failed", value: "PAYMENT_FAILED" },
        { text: "Pending", value: "PAYMENT_PENDING" },
      ],
    };
  },
  mounted() {
    this.getData();
  },
  beforeDestroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  },
  methods: {
    onSearchInput() {
      // Clear previous timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      
      // Set new timeout for debounced search
      this.searchTimeout = setTimeout(() => {
        this.page = 1;
        this.getData();
      }, 500); // 500ms delay
    },
    
    async getData() {
      this.isLoading = true;
      try {
        let query = new URLSearchParams({
          page: this.page,
          rowsPerPage: 10,
        });

        if (this.filter.search_keyword) {
          query.append("search_keyword", this.filter.search_keyword);
        }
        if (this.filter.status) {
          query.append("code", this.filter.status);
        }
        if (this.filter.date) {
          // Ensure date is in YYYY-MM-DD format
          const formattedDate = this.filter.date.split('T')[0]; // Remove time part if present
          query.append("date_of_payment", formattedDate);
        }

        const response = await axios.get(`payment_receipts?${query.toString()}`);
        this.paymentReceipts = response.data.data || [];
        this.count = response.data.count || 0;
      } catch (error) {
        console.error("Error fetching payment receipts:", error);
        this.$toast.error("Failed to fetch payment receipts");
      } finally {
        this.isLoading = false;
      }
    },
    getStatusColor(code) {
      switch (code) {
        case "PAYMENT_SUCCESS":
          return "green";
        case "PAYMENT_PENDING":
          return "orange";
        case "PAYMENT_FAILED":
          return "red";
        default:
          return "grey";
      }
    },
    getStatusText(code) {
      switch (code) {
        case "PAYMENT_SUCCESS":
          return "Success";
        case "PAYMENT_PENDING":
          return "Pending";
        case "PAYMENT_FAILED":
          return "Failed";
        default:
          return "Unknown";
      }
    },
    getModeOfPaymentText(mode) {
      switch (mode) {
        case 1:
          return "Cash";
        case 2:
          return "Online";
        case 3:
          return "Wallet";
        case 4:
          return "Credit";
        default:
          return "N/A";
      }
    },
    formatDate(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-IN", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
  },
};
</script>